import React, { useEffect, useState } from "react";
import ActionList from "../components/ActionList";
import ActionEdit from "../components/ActionEdit";
import { Card, Modal } from "react-bootstrap";
import { Action, convertActionToRecommended } from "../types/Action";
import "./action-list.css";
import { UserAction } from "../types/UserAction";
import { auth } from "../../config/firebase";
import axios from "axios";
import RandomActionGenerator from "../utils/randomActionGenerator";
import ActionsExportToExcel from "../components/ActionsExcelExport";
import ActionListHeader from "../components/ActionListHeader";
import FeedbackSurveyModal from "../../common/components/feedback/FeedbackSurveyModal";
import EventDetails from "event/components/EventDetails";
import RightOverlay from "../../common/components/overlay/RightOverlay";
import openAiActions from "../utils/openAiActions";
import SdgPickerContainer from "actions/components/sdg/SdgPickerContainer";
import ActionPageInfo from "actions/components/ActionPageInfo";
import useUserProfile from "profile/hooks/useProfile";
import useEvent from "event/hooks/useEvent";
import useQueryParameter from "common/url/useQueryParameter";
import SearchBox from "common/components/search/SearchBox";
import ActionButtons from "actions/components/ActionButtons";
import useUserSDG from "sdgselection/hooks/userSDGs";
import { sdgListGetter } from "common/data/sdgListGetter";
import { SdgHeader } from "actions/types/Sdg";
import { RecommendedActionsCache } from "actions/utils/recommendedActionsCache";
import { actionAPI } from "actions/utils/actionAPI";

export const ActionListPage = () => {

  const defaultSdg = 1;
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSwitchSdgModal, setShowSwitchSdgModal] = useState(false);
  const [selectedSdg, setSelectedSdg] = useState<number | undefined>(defaultSdg);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const eventId = useQueryParameter("eventId");
  const { userProfile, loading, error } = useUserProfile();
  const { userSDGList, allSdgIds } = useUserSDG();
  const { event: eventData, loading:eventLoading, error:eventLoadingError } = useEvent(eventId);

  const [randomActionGenerator, setRandomActionGenerator] = useState<any>(null);

  const [actionList, setActionList] = useState([] as Action[]);
  const [filteredActionList, setFilteredActionList] = useState([] as Action[]);
  const [index, setIndex] = useState<number>(-1);
  const [UserAction, setUserAction] = useState<UserAction>({} as UserAction);
  const [sdgHeader, setSdgHeader] = useState<SdgHeader | null>(null);
  const [showMoreInfo, setShowMoreInfo] = useState<boolean>(false);

  useEffect(() => {
    if(userSDGList){
        const selectedUserSdgs = userSDGList.find(us => us._id === eventData?.userSdgId) || userSDGList[0];
        const sdgHeaders = sdgListGetter(selectedUserSdgs.sdgs);
        setSdgHeader(sdgHeaders[0]);
    }
    else{
      const sdgHeaders = sdgListGetter(allSdgIds);
      setSdgHeader(sdgHeaders[0]);
    }
    
  }, [userSDGList, eventData]); // The empty dependency array ensures the effect runs only once on mount

  const handleOpenFeedbackModal = () => {
    setShowFeedbackModal(true);
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
  };

  const onHandleSwitchSdg = () => {
    setShowSwitchSdgModal(true);
  };

  const updateFilteredList = (list: Action[]) =>{
    setFilteredActionList(filterActionList(searchQuery || "", list));
  }

  const handleDeleteAction = async (index: number, eventId: string) => {
    const thisAction = actionList && actionList.length >= 0 ? actionList[index] : null;
    if (thisAction) {
      if(thisAction.id){
        try{
          await actionAPI.DeleteUserAction(thisAction.id);
        }
        catch(e:any){}
      }
      const updatedItems = [...actionList];
      updatedItems.splice(index, 1);
      setActionList(updatedItems);
      RecommendedActionsCache.add(convertActionToRecommended(thisAction, eventId));
    }
  };

  const handleEditAction = (index: number, item: Action) => {
    setIndex(index);
    setUserAction({ ...item, userId: userId });
    setIsEditOpen(true);
  };

  const handleCreateAction = async (index: number, item: Action) => {
    setIndex(index);
    try{
      setIsSaving(true);
      
      if (selectedSdg && !item.sdg) item.sdg = selectedSdg;
      const response = await actionAPI.CreateUserAction({ ...item, userId: userId, eventId: eventId })
      const userAction: UserAction = response.userAction;
      
      setActionList((prevActionList) => {
        let updatedList = [...prevActionList];
        if(index >= 0) {
          updatedList[index] = userAction;
        }
        else{
          updatedList = [userAction, ...updatedList];
        }
        return updatedList;
      });
    }
    catch(e){ setErrorMessage(`${e}`); }
    finally{  
      setIsSaving(false);
    }
  };

  const handleUpdateUserAction = async (index: number | null, data: UserAction) => {
    setTimeout(() => setIsEditOpen(false), 400);

    try {
      setIsSaving(true);
      const actionIsNew = !data.id;
      if (selectedSdg && !data.sdg) data.sdg = selectedSdg;
      let response: any = null;

      response = data.id
        ? await actionAPI.UpdateUserAction(data, data.id)
        : await actionAPI.CreateUserAction(data);

      const userAction: UserAction = response.userAction;

      setActionList((prevActionList) => {
        let updatedActionList = prevActionList.map((action) => {
          return action.id === userAction.id ? userAction : action;
        });

        // If the action doesn't exist in the array, add it.. i..e new
        if (index === null || index < 0) {
          updatedActionList = [userAction, ...updatedActionList];
        }
        else{          
          updatedActionList[index] = userAction;
        }

        return updatedActionList;
      });

      setIndex(-1);
    } catch (e) {
      setErrorMessage(`${e}`);
    } finally{
      setIsSaving(false);
    }
  };

  const handleDeleteUserAction = (index: number) => {
    if (index >= 0) handleDeleteAction(index, eventId!);
  };

  const handleCancelEditModal = () => {
    setIsEditOpen(false);
  };

  const getActions = async () => {

    try {
      setIsLoading(true);
      let rndAGen: any;

      if(!randomActionGenerator){
        const currSdg = selectedSdg ? selectedSdg : defaultSdg;
        rndAGen = new RandomActionGenerator(currSdg, true);
        setRandomActionGenerator(rndAGen);
      }
      else{
        rndAGen = randomActionGenerator;
      }

      const globalActions: any[] = rndAGen.getRandomActions(2);
      const aiActions: any[] = [await openAiActions.getActionBySdg(selectedSdg as number, globalActions, RecommendedActionsCache.getAllActions(eventId))];
      
      setActionList([...globalActions, ...aiActions,...actionList]);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const toggleEditComponent = () => {
    setIsEditOpen(!isEditOpen);
  };

  const createCustomAction = () => {
    setIsEditOpen(false);
    setIndex(-1);
    setUserAction({ userId: userId } as UserAction);
    setTimeout(() => setIsEditOpen(true), 400);
  };

  const filterActionList = (searchQuery:string, list: Action[]) =>{
    const searchQ = (searchQuery || '').toLocaleLowerCase();
    return list.filter(action =>
      action.title?.toLocaleLowerCase().includes(searchQ) || 
      action.description?.toLocaleLowerCase().includes(searchQ)
    );
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        const userId = user.uid;
        setUserId(userId);
        // Use the userId in your application logic
      } else {
        // User is not logged in
        console.log("No user logged in");
      }
    });

    return () => unsubscribe(); // Unsubscribe from the observer when component unmounts
  }, []);

  useEffect(() => {
    if (userId) {
      actionAPI.getActionsByUser(userId, eventId!)
        .then((data) => {
          setActionList(data.records as UserAction[]);
        })
        .catch((error) => console.error("Error fetching data:", error));  
    }
  }, [userId, eventId]);

  useEffect(() => {
    if(searchQuery && searchQuery.length > 0){
      const filteredList = filterActionList(searchQuery, actionList);
      setFilteredActionList(filteredList);
    }
    else{
      setFilteredActionList(actionList);
    }
  },[searchQuery]);

  useEffect(() =>{
    //display more info if there is no action list
    setShowMoreInfo(actionList && actionList.length === 0);
    updateFilteredList(actionList);
  }, [actionList]);

  const handleSdgChange = (sdg: number | undefined) => {
    if(!sdg) return;
    setSelectedSdg(undefined);
    setRandomActionGenerator(new RandomActionGenerator(sdg || defaultSdg, true));
    const sdgHeaders = sdgListGetter([sdg]);

    setSdgHeader(sdgHeaders[0]);
    setTimeout(() => setSelectedSdg(sdg), 1000);
    setShowSwitchSdgModal(false);
  };

  return (
    <div>
      <>
        <div className='p-1 card-body'>
          {eventLoading && <div>Event Loading...</div>}
          {eventLoadingError && <div>{error}</div>}

          {eventId && eventData &&
            <EventDetails 
              eventData={eventData} 
              preText={`${actionList && actionList.length > 0? actionList.length: ""} Actions for event: `}
              showSubDetails={true} />
          }
          {selectedSdg && (
            <ActionListHeader 
              sdg={selectedSdg ? selectedSdg : defaultSdg} 
              sdgHeader={sdgHeader}
              changeSdg={onHandleSwitchSdg} 
              onShowMore={() => setShowMoreInfo(!showMoreInfo)}
              eventId={eventId || undefined}
              canChange={true}
              />
          )}
        </div>
      </>
      
      <div className="mt-2">
        <div className="row">
          <div className="col-4">
            {(actionList && actionList.length > 0) &&
              <SearchBox 
                searchQuery={searchQuery} 
                setSearchQuery={setSearchQuery} 
                placeholder={`Search within all ${actionList.length} actions...`}
              />
            }
          </div>
          <div className="col-2">
            {searchQuery &&      
              <div className="mt-2">
                {filteredActionList.length} / {actionList.length} <strong>Found</strong>  
              </div>   
            }
          </div>
          <div className="col-6 d-flex justify-content-end">
            <ActionButtons
              getActions={getActions}
              createCustomAction={createCustomAction}
              handleOpenFeedbackModal={handleOpenFeedbackModal}
              isLoading={isLoading}
              actionList={actionList}
              hideExport={true} />
          </div>
        </div>
      </div>

      {actionList && actionList.length > 0 && (
        <ActionList
          key={actionList.length}
          data={searchQuery && searchQuery.length > 0? filteredActionList: actionList}
          onDeleteItem={handleDeleteAction}
          onEditItem={handleEditAction}
          onCreateItem={handleCreateAction}
          isSaving={isSaving}
          eventId={eventId!}
        />
      )}
      <div className='container-fluid bottom-actions mt-4'>
        <ActionButtons
          getActions={getActions}
          createCustomAction={createCustomAction}
          handleOpenFeedbackModal={handleOpenFeedbackModal}
          isLoading={isLoading}
          actionList={actionList}
        />
      </div>
      <RightOverlay 
        onClose={handleCancelEditModal}
        isOpen={isEditOpen}
        children={
          <div>
              {isEditOpen && (
              <ActionEdit
                userProfile={userProfile}
                event={eventData}
                data={UserAction}
                onCancel={handleCancelEditModal}
                onUpdate={handleUpdateUserAction}
                onCreate={handleCreateAction}
                onDelete={() => {}}
                index={index}
                isSaving={isSaving}
              />
            )}
          </div>
        }
      /> 
      <RightOverlay 
        onClose={()=> setShowMoreInfo(false)}
        isOpen={showMoreInfo}
        children={
          <div>
              {showMoreInfo && (
              <ActionPageInfo
                sdg={selectedSdg ? selectedSdg : defaultSdg} 
                sdgHeader={sdgHeader}
              />
            )}
          </div>
        }
      />      
      <div>
        {showFeedbackModal && (
          <FeedbackSurveyModal
            onDoneComponent={<ActionsExportToExcel data={actionList} />}
            onCancel={() => setShowFeedbackModal(false)}
          />
        )}
      </div>
      <div>
        <RightOverlay 
          onClose={() => setShowSwitchSdgModal(false)}
          isOpen={showSwitchSdgModal}
          children={
            <div>
                {showSwitchSdgModal && (
                 <SdgPickerContainer 
                    userSdgId={eventData?.userSdgId}
                    currentSdg={selectedSdg || defaultSdg}
                    onCancel={() => setShowSwitchSdgModal(false)} 
                    onApply={handleSdgChange} />  
                )}
            </div>
          }
        />  
      </div>
    </div>
  );
};
